import { useState } from 'react';
import { useField } from 'formik';
import { StylesConfig } from 'react-select';
import { AsYouType } from 'libphonenumber-js';
import { Select } from '@round/ui-kit';
import { flagEmoji } from './LocationStep';
import { waveSelectStyles } from './waveSelectStyles';
import { DIAL_COUNTRIES, DialCountry, asCountryCode, parsePhoneNumber } from './dialCodes';
import { FieldError } from './steps';

// Country picker sits flush to the left of the number input: no right radius, a
// shared border, and a menu wide enough for full country names.
const countrySelectStyles: StylesConfig = {
    ...waveSelectStyles,
    control: (base, state) => ({
        ...(waveSelectStyles.control?.(base, state) as object),
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderRight: 0,
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 4px 2px 12px' }),
    menu: (base, state) => ({
        ...(waveSelectStyles.menu?.(base, state) as object),
        width: 'max-content',
        minWidth: '16rem',
    }),
};

const optionLabel = (country: DialCountry) => `${country.name} +${country.dial}`;

export const PhoneField = () => {
    const [{ value }, , helpers] = useField<string>('phoneNumber');
    // Local country/national split, seeded once from the stored E.164 value so the
    // fields survive step navigation. The formik value stays the source of truth.
    const [{ country, national }, setLocal] = useState(() => parsePhoneNumber(value));

    const commit = (nextCountry: DialCountry, nextNational: string) => {
        // Country-aware normalisation: strips the national trunk prefix where the
        // country uses one (e.g. UK "020 1234 5678" → "+442012345678"), keeps it
        // where the country retains it (e.g. Italy), and ignores non-numeric input.
        // The displayed national digits and the stored E.164 stay in sync, and the
        // field stays optional: nothing parseable means no phone number.
        const asYouType = new AsYouType(asCountryCode(nextCountry));
        asYouType.input(nextNational);
        const phone = asYouType.getNumber();
        const national = phone ? phone.nationalNumber : nextNational.replace(/\D/g, '');
        setLocal({ country: nextCountry, national, unrecognised: false });
        helpers.setValue(phone ? phone.number : '');
    };

    return (
        <div className="wm-field">
            <span className="wm-field__label">Phone number (optional)</span>
            <div className="flex items-stretch">
                <div className="w-[8.5rem] shrink-0">
                    <Select
                        aria-label="Country dialling code"
                        menuPosition="fixed"
                        captureMenuScroll={false}
                        styles={countrySelectStyles}
                        options={DIAL_COUNTRIES}
                        value={country}
                        getOptionValue={(option) => (option as DialCountry).code}
                        getOptionLabel={(option) => optionLabel(option as DialCountry)}
                        formatOptionLabel={(data, meta) => {
                            const option = data as DialCountry;
                            return meta.context === 'value' ? (
                                <span>
                                    {flagEmoji(option.code)} +{option.dial}
                                </span>
                            ) : (
                                <span>
                                    {flagEmoji(option.code)} {option.name}{' '}
                                    <span className="text-wm-ink-3">+{option.dial}</span>
                                </span>
                            );
                        }}
                        onChange={(option) => option && commit(option as DialCountry, national)}
                    />
                </div>
                <input
                    className="wm-input"
                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    aria-label="Phone number"
                    placeholder="Phone number"
                    value={national}
                    onChange={(event) => commit(country, event.target.value)}
                    onBlur={() => helpers.setTouched(true)}
                />
            </div>
            <FieldError name="phoneNumber" />
        </div>
    );
};

export default PhoneField;
